'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SouthIcon from '@mui/icons-material/South';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';

import { useTheme } from '@/contexts/ThemeContext';
import { useAuth, useBodyScrollLock, useOfframp, useT } from '@/hooks';
import { useBridgeWithdrawal } from '@/hooks/useBridgeWithdrawal';
import { walletApi } from '@/services/api/wallet.api';
import { usePortfolioSummary } from '@/services/query';
import { openModal, useAppDispatch } from '@/store';
import type { BridgeOutboundRoute, BridgeQuote } from '@/types/withdrawal.types';
import { startSessionRecording, stopSessionRecording } from '@/utils/mixpanel';

import { API_BASE_URL, ONRAMPER_PAYMENTS_ENABLED } from '@/constants';

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialWithdrawalId?: string;
  initialOwnerAddress?: string;
}

type Screen = 'select' | 'bank' | 'wallet';
type WalletStep = 'form' | 'confirm';

const QUOTE_DEBOUNCE_MS = 600;
const RECENT_ADDR_LIMIT = 5;
const ALLOWED_SYMBOLS = [
  'USDC',
  'USDC.E',
  'ARB',
  'BNB',
  'BTCB',
  'BUSD',
  'CBBTC',
  'DAI',
  'ETH',
  // 'MATIC',
  // 'POL',
  // 'SOL',
  'USDE',
  'USDT',
  'WBNB',
  'WETH',
];

export function WithdrawModal({
  isOpen,
  onClose,
  initialWithdrawalId,
  initialOwnerAddress,
}: WithdrawModalProps) {
  const { t } = useT();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { mode: themeMode } = useTheme();
  const { data: portfolioSummary } = usePortfolioSummary();
  const cashBalance = portfolioSummary?.cashBalance ?? 0;

  useBodyScrollLock(isOpen);

  const {
    step: offrampStep,
    txHash: offrampTxHash,
    error: offrampError,
    pendingTx,
    completeOfframp,
    isPending,
    reset: resetOfframp,
    wallets,
  } = useOfframp();

  const {
    step: bridgeStep,
    transactionHash: bridgePolygonTxHash,
    error: bridgeError,
    executeBridgeWithdrawal,
    reset: resetBridge,
  } = useBridgeWithdrawal();

  const signingWallet =
    wallets.find((w) => w.walletClientType === 'privy' || w.connectorType === 'embedded') ||
    wallets[0] ||
    null;
  const isWalletMissing = wallets.length === 0;

  // ──────────────────────────────────────────────────────────────────────────
  // Screen state
  // ──────────────────────────────────────────────────────────────────────────
  const [screen, setScreen] = useState<Screen>(initialWithdrawalId ? 'bank' : 'select');
  const [walletStep, setWalletStep] = useState<WalletStep>('form');

  // Confirm-on-close mini dialog
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  // Wallet form state
  // Token-first model: user picks symbol → then picks chain.
  // tokenAddress is derived from (tokenSymbol + chainId).
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState(''); // e.g. "USDC"
  const [chainId, setChainId] = useState('');
  const [routes, setRoutes] = useState<BridgeOutboundRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [quote, setQuote] = useState<BridgeQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [recentAddrs, setRecentAddrs] = useState<string[]>([]);

  const isWalletDirty =
    recipient.trim().length > 0 ||
    Number(amount) > 0 ||
    walletStep !== 'form' ||
    bridgeStep !== 'idle';

  const hardClose = useCallback(() => {
    resetOfframp();
    resetBridge();
    setScreen(initialWithdrawalId ? 'bank' : 'select');
    setWalletStep('form');
    setRecipient('');
    setAmount('');
    setTokenSymbol('');
    setChainId('');
    setQuote(null);
    setCloseConfirmOpen(false);
    onClose();
  }, [onClose, resetOfframp, resetBridge, initialWithdrawalId]);

  const requestClose = useCallback(() => {
    // Mid-signing on either flow → never silently close
    const bridgeInFlight =
      bridgeStep === 'preparing' || bridgeStep === 'submitting' || bridgeStep === 'polling';
    const bankInFlight = ['loading_tx', 'awaiting_sign', 'confirming'].includes(offrampStep);
    if (bridgeInFlight || bankInFlight || isWalletDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    hardClose();
  }, [bridgeStep, offrampStep, isWalletDirty, hardClose]);

  // Start/stop session recording with the modal
  useEffect(() => {
    if (isOpen) {
      startSessionRecording();
    } else {
      stopSessionRecording();
    }
  }, [isOpen]);

  // Auto-launch bank signing if redirected with a withdrawalId
  useEffect(() => {
    if (isOpen && initialWithdrawalId && offrampStep === 'idle') {
      setScreen('bank');
      completeOfframp(initialWithdrawalId, initialOwnerAddress || undefined);
    }
  }, [isOpen, initialWithdrawalId, initialOwnerAddress, offrampStep, completeOfframp]);

  // Load bridge routes when wallet screen opens
  useEffect(() => {
    if (!isOpen || screen !== 'wallet') return;
    let cancelled = false;
    (async () => {
      setRoutesLoading(true);
      try {
        const list = await walletApi.getBridgeOutboundOptions();
        if (!cancelled) {
          const filtered = list.filter((r) => ALLOWED_SYMBOLS.includes(r.symbol.toUpperCase()));
          setRoutes(filtered);
          // Auto-select default token (USDC if available, else first unique symbol)
          if (!tokenSymbol && list.length > 0) {
            const usdcRoute = list.find((r) => r.symbol.toUpperCase() === 'USDC');
            const defaultSymbol = usdcRoute?.symbol ?? list[0].symbol;
            setTokenSymbol(defaultSymbol);
            // Auto-select Polygon as default chain if it supports this token, else first
            const routesForSymbol = list.filter((r) => r.symbol === defaultSymbol);
            const polygonRoute = routesForSymbol.find((r) => r.chainId === '137');
            setChainId((polygonRoute ?? routesForSymbol[0]).chainId);
          }
        }
      } catch {
        if (!cancelled) setRoutes([]);
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, screen]);

  // ── Derived maps (token-first model) ──────────────────────────────────────

  // Unique token symbols sorted: stablecoins first, then alphabetically
  const uniqueTokens = useMemo(() => {
    const map = new Map<string, { symbol: string }>();
    for (const r of routes) {
      if (!map.has(r.symbol)) map.set(r.symbol, { symbol: r.symbol });
    }
    const STABLE_PRIORITY = ['USDC', 'USDT', 'DAI', 'PUSD', 'PYUSD'];
    return Array.from(map.values()).sort((a, b) => {
      const ai = STABLE_PRIORITY.indexOf(a.symbol.toUpperCase());
      const bi = STABLE_PRIORITY.indexOf(b.symbol.toUpperCase());
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.symbol.localeCompare(b.symbol);
    });
  }, [routes]);

  // All unique chains (for chain dropdown — some may not support the selected token)
  const allChains = useMemo(() => {
    const map = new Map<string, { chainId: string; chainName: string }>();
    for (const r of routes) {
      if (!map.has(r.chainId)) map.set(r.chainId, { chainId: r.chainId, chainName: r.chainName });
    }
    return Array.from(map.values()).sort((a, b) => a.chainName.localeCompare(b.chainName));
  }, [routes]);

  // Chains that support the currently selected token
  const chainsForToken = useMemo(
    () => new Set(routes.filter((r) => r.symbol === tokenSymbol).map((r) => r.chainId)),
    [routes, tokenSymbol]
  );

  // The full resolved route for the current (symbol + chainId) selection
  const selectedToken = useMemo(
    () => routes.find((r) => r.symbol === tokenSymbol && r.chainId === chainId) ?? null,
    [routes, tokenSymbol, chainId]
  );

  const selectedChain = useMemo(
    () => allChains.find((c) => c.chainId === chainId) ?? null,
    [allChains, chainId]
  );

  // When token changes, keep chain if it supports the new token; else auto-pick first supported chain
  useEffect(() => {
    if (!tokenSymbol || routes.length === 0) return;
    if (!chainsForToken.has(chainId)) {
      const routesForSymbol = routes.filter((r) => r.symbol === tokenSymbol);
      const polygonRoute = routesForSymbol.find((r) => r.chainId === '137');
      setChainId((polygonRoute ?? routesForSymbol[0]).chainId);
      setQuote(null);
    }
  }, [tokenSymbol, chainsForToken, chainId, routes]);

  // Load recent addresses for the current chain
  useEffect(() => {
    setRecentAddrs(chainId ? getRecentAddresses(chainId) : []);
  }, [chainId]);

  // ──────────────────────────────────────────────────────────────────────────
  // Validation
  // ──────────────────────────────────────────────────────────────────────────
  const addrValidation = useMemo(
    () => validateAddress(recipient, selectedChain?.chainName ?? ''),
    [recipient, selectedChain]
  );
  const showAddrError = recipient.trim().length > 0 && !addrValidation.valid;

  const minCheckoutUsd =
    selectedToken?.minCheckoutUsd !== undefined ? selectedToken.minCheckoutUsd + 3 : 0;
  const amountNum = Number(amount);
  const isAddressValid = addrValidation.valid;
  const isAmountValid =
    amountNum > 0 && amountNum <= cashBalance && amountNum >= Math.max(minCheckoutUsd, 0);
  // isFormValid requires a resolved selectedToken (symbol + chain combo exists in routes)
  const isFormValid = isAddressValid && isAmountValid && !!selectedToken;

  // Debounced live quote
  useEffect(() => {
    if (!isFormValid || !selectedToken) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const q = await walletApi.getBridgeQuote({
          amount: amountNum,
          toChainId: selectedToken.chainId,
          toTokenAddress: selectedToken.tokenAddress,
          recipientAddress: recipient.trim(),
        });
        setQuote(q);
      } catch {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [isFormValid, amountNum, selectedToken, recipient]);

  // ──────────────────────────────────────────────────────────────────────────
  // Derived UI state
  // ──────────────────────────────────────────────────────────────────────────
  const onramperUrl = useMemo(() => {
    const widgetBase = process.env.NEXT_PUBLIC_ONRAMPER_WIDGET_URL || 'https://buy.onramper.com';
    const apiKey = process.env.NEXT_PUBLIC_ONRAMPER_API_KEY || 'pk_test_01KFYHHE0M2EB8G0XHW0568A78';
    const targetWallet = user?.proxyWalletAddress;
    const appUserId = user?.id;
    const params = new URLSearchParams({
      apiKey,
      mode: 'sell',
      offrampCashoutRedirectUrl: `${API_BASE_URL}/api/v1/withdrawals-v3/bank/onramper-callback`,
      partnerContext: JSON.stringify({ appUserId, walletAddress: targetWallet }),
      sell_defaultFiat: 'USD',
      sell_defaultCrypto: 'USDC_POLYGON',
      sell_onlyCryptos: 'USDC_POLYGON', // Disables crypto selection by limiting to one option
      sell_onlyCryptoNetworks: 'polygon', // Forces Polygon network
      sell_isAmountEditable: 'true',
      sell_defaultWallet: targetWallet ?? '',
      themeName: themeMode === 'dark' ? 'dark' : 'light',
      sell_maxAvailableCrypto: cashBalance.toString(),
      enableCountrySelector: 'true',
    });
    return `${widgetBase}?${params.toString()}`;
  }, [user, themeMode, cashBalance]);

  const bankIsSigning =
    ['loading_tx', 'awaiting_sign', 'confirming'].includes(offrampStep) || isPending;
  const bankShowSigningUi = offrampStep !== 'idle';
  const walletIsBusy = bridgeStep !== 'idle';

  // Quote display values
  const tokenDecimals = getTokenDecimals(selectedToken?.symbol ?? '');
  const estReceiveAmount = quote?.estToTokenBaseUnit
    ? Number(quote.estToTokenBaseUnit) / 10 ** tokenDecimals
    : 0;
  const estOutputUsd = typeof quote?.estOutputUsd === 'number' ? quote.estOutputUsd : null;
  const etaMin =
    typeof quote?.estCheckoutTimeMs === 'number'
      ? Math.ceil(quote.estCheckoutTimeMs / 60_000)
      : null;
  const feeBreakdown = quote?.estFeeBreakdown as Record<string, unknown> | undefined;
  const feeUsd =
    feeBreakdown && typeof feeBreakdown.totalImpactUsd === 'number'
      ? (feeBreakdown.totalImpactUsd as number)
      : feeBreakdown && typeof feeBreakdown.gasFeeUsd === 'number'
        ? (feeBreakdown.gasFeeUsd as number)
        : null;

  // ──────────────────────────────────────────────────────────────────────────
  // CTA state machine
  // ──────────────────────────────────────────────────────────────────────────
  const ctaLabel = (() => {
    if (isWalletMissing)
      return t('withdrawModal.walletNotConnected', { defaultValue: 'Wallet not connected' });
    if (!recipient.trim()) return t('withdrawModal.enterRecipientAddress');
    if (showAddrError)
      return t('withdrawModal.invalidAddress', { defaultValue: 'Invalid address' });
    if (!amount || amountNum <= 0) return t('withdrawModal.amount');
    if (amountNum > cashBalance)
      return t('withdrawModal.insufficientBalance', { defaultValue: 'Insufficient balance' });
    if (amountNum < minCheckoutUsd)
      return t('withdrawModal.minCheckout', {
        min: minCheckoutUsd.toFixed(2),
        defaultValue: `Min $${minCheckoutUsd.toFixed(2)}`,
      });
    if (!tokenSymbol || !chainId)
      return t('withdrawModal.selectTokenAndChain', { defaultValue: 'Select token & chain' });
    return t('common.continue', { defaultValue: 'Continue' });
  })();

  const ctaEnabled = isFormValid && !isWalletMissing && !walletIsBusy;

  const goToConfirm = () => {
    if (!ctaEnabled || !selectedToken || !signingWallet?.address) return;
    setWalletStep('confirm');
  };

  const submit = () => {
    if (!ctaEnabled || !selectedToken || !signingWallet?.address) return;
    saveRecentAddress(chainId, recipient.trim());
    executeBridgeWithdrawal({
      amount: amountNum,
      toChainId: selectedToken.chainId,
      toTokenAddress: selectedToken.tokenAddress,
      recipientAddress: recipient.trim(),
      ownerAddress: signingWallet.address,
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  const paperBg = 'var(--bg-primary)';
  const hasFunds = cashBalance >= 0.001;

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') requestClose();
      }}
      disableEscapeKeyDown
      fullScreen={isMobile && screen !== 'select'}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? (screen === 'select' ? '92%' : '100%') : 480,
          maxHeight: isMobile ? (screen === 'select' ? 'auto' : '100%') : '92vh',
          borderRadius: isMobile ? (screen === 'select' ? '24px' : 0) : '24px',
          bgcolor: 'var(--bg-primary)',
          border: isMobile && screen !== 'select' ? 'none' : '1px solid var(--border-light)',
          backgroundImage: 'none',
          boxShadow: isMobile
            ? 'none'
            : themeMode === 'dark'
              ? '0 24px 48px rgba(0,0,0,0.5)'
              : '0 12px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          p: isMobile && screen !== 'select' ? 0 : 3,
          m: isMobile && screen === 'select' ? 2 : 0,
        },
      }}
      sx={{
        zIndex: 10001,
        '& .MuiDialog-container': {
          zIndex: 10001,
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: isMobile ? '100%' : screen === 'bank' && !bankShowSigningUi ? 650 : 'auto',
          minHeight: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : '92vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: paperBg,
          overflow: 'hidden',
        }}
      >
        {/* Header - Centered for Hub, Navigable for sub-screens */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            px: 0,
            py: 1,
            mb: 1,
            flexShrink: 0,
          }}
        >
          {screen !== 'select' && !bankShowSigningUi && !walletIsBusy && (
            <IconButton
              size="small"
              onClick={() => {
                if (screen === 'wallet' && walletStep === 'confirm') {
                  setWalletStep('form');
                  return;
                }
                setScreen('select');
                resetOfframp();
                resetBridge();
              }}
              sx={{
                position: 'absolute',
                left: 0,
                color: 'var(--text-muted)',
                '&:hover': { color: 'var(--text-primary)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          <Typography
            variant="h6"
            className="text-lg font-bold text-[var(--text-primary)]"
            sx={{ fontSize: '1.125rem' }}
          >
            {screen === 'select'
              ? t('withdrawModal.withdrawFunds')
              : screen === 'wallet' && walletStep === 'confirm'
                ? t('withdrawModal.review', { defaultValue: 'Review' })
                : t('withdrawModal.withdraw')}
          </Typography>

          <button
            type="button"
            onClick={requestClose}
            className="absolute right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </Box>

        {/* ── Screen 1: method selection / empty balance ───────────────────── */}
        <ScreenSwitcher show={screen === 'select'}>
          {!hasFunds ? (
            <Box
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
                textAlign: 'center',
                overflowY: 'auto',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'rgba(16,185,129,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 32, color: '#10B981' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="var(--text-primary)">
                {t('withdrawModal.emptyStateTitle')}
              </Typography>
              <Typography variant="body2" color="var(--text-muted)" sx={{ maxWidth: 320 }}>
                {t('withdrawModal.emptyStateDesc')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<SouthIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  hardClose();
                  if (!user?.isReferredUser) {
                    dispatch(openModal('referral'));
                  } else {
                    dispatch(openModal('deposit'));
                  }
                }}
                sx={{
                  mt: 1,
                  bgcolor: '#0085ff',
                  color: 'white',
                  '&:hover': { bgcolor: '#0074e0' },
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.2,
                  px: 4,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                }}
              >
                {t('withdrawModal.depositFunds')}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                px: 0,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Typography className="mb-3 text-center text-sm text-[var(--text-muted)]">
                  {t('withdrawModal.selectMethod')}
                </Typography>
                <BalancePill cashBalance={cashBalance} />
              </Box>

              <MethodButton
                icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />}
                title={t('withdrawModal.transferBank')}
                subtitle={
                  ONRAMPER_PAYMENTS_ENABLED
                    ? t('withdrawModal.transferBankDesc')
                    : t('withdrawModal.temporarilyUnavailable', {
                        defaultValue: 'Temporarily unavailable',
                      })
                }
                onClick={() => setScreen('bank')}
                disabled={!ONRAMPER_PAYMENTS_ENABLED}
              />
              <MethodButton
                icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />}
                title={t('withdrawModal.transferWallet')}
                subtitle={t('withdrawModal.transferWalletDesc')}
                onClick={() => setScreen('wallet')}
              />
            </Box>
          )}
        </ScreenSwitcher>

        {/* ── Screen 2: bank flow ──────────────────────────────────────────── */}
        <ScreenSwitcher show={screen === 'bank'}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            {offrampError && (
              <Box
                sx={{
                  p: 2,
                  m: 2,
                  bgcolor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="var(--text-primary)" sx={{ opacity: 0.9 }}>
                  ⚠️ {offrampError}
                </Typography>
                <Button
                  size="small"
                  onClick={() => resetOfframp()}
                  sx={{ mt: 1, textTransform: 'none', color: 'var(--text-primary)' }}
                >
                  {t('common.retry', { defaultValue: 'Try again' })}
                </Button>
              </Box>
            )}

            {!bankShowSigningUi && (
              <Box sx={{ flex: 1, width: '100%', minHeight: 600 }}>
                <iframe
                  src={onramperUrl}
                  title="Onramper Sell Widget"
                  style={{ width: '100%', height: '100%', border: 'none', minHeight: 600 }}
                  allow="accelerometer; autoplay; camera; gyroscope; payment; microphone"
                />
              </Box>
            )}

            {bankShowSigningUi && offrampStep !== 'completed' && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  px: 3,
                }}
              >
                {bankIsSigning ? (
                  <>
                    <CircularProgress size={48} sx={{ color: '#10B981', mb: 3, mx: 'auto' }} />
                    <Typography variant="h6" fontWeight={700} color="var(--text-primary)">
                      {offrampStep === 'loading_tx' &&
                        t('withdrawModal.fetchingDetails', {
                          defaultValue: 'Fetching transfer details…',
                        })}
                      {offrampStep === 'awaiting_sign' &&
                        t('withdrawModal.awaitingSignature', {
                          defaultValue: 'Awaiting signature',
                        })}
                      {offrampStep === 'confirming' &&
                        t('withdrawModal.finalizingPayout', { defaultValue: 'Finalizing payout' })}
                    </Typography>
                    <Typography variant="body2" color="var(--text-muted)" sx={{ mt: 1 }}>
                      {offrampStep === 'awaiting_sign'
                        ? t('withdrawModal.approveTransaction', {
                            defaultValue: 'Please approve the transaction in the Privy prompt.',
                          })
                        : t('withdrawModal.takeMoments', {
                            defaultValue:
                              'This may take a few moments. Please do not close this window.',
                          })}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" color="var(--text-primary)" sx={{ mb: 2 }}>
                      {t('withdrawModal.readyToFinalize', {
                        defaultValue: 'Ready to finalize transfer',
                      })}
                    </Typography>
                    {isWalletMissing && (
                      <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                        {t('withdrawModal.noEmbeddedWallet', {
                          defaultValue: '🚫 No embedded wallet found. Please log in.',
                        })}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (initialWithdrawalId) {
                          completeOfframp(initialWithdrawalId, initialOwnerAddress || undefined);
                        }
                      }}
                      disabled={!initialWithdrawalId || isWalletMissing}
                      sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' }, mx: 'auto' }}
                    >
                      {t('withdrawModal.signAndSend', { defaultValue: 'Sign & send crypto' })}
                    </Button>
                  </>
                )}
                {pendingTx?.meta && (
                  <Box
                    sx={{
                      mt: 4,
                      p: 2,
                      bgcolor: 'var(--bg-secondary)',
                      borderRadius: 2,
                      textAlign: 'left',
                    }}
                  >
                    <Row
                      label={t('withdrawModal.sellAmount', { defaultValue: 'Sell amount' })}
                      value={`${pendingTx.meta.inAmount} ${pendingTx.meta.sourceCurrency?.split('_')[0] || 'pUSD'}`}
                    />
                    <Row
                      label={t('withdrawModal.network', { defaultValue: 'Network' })}
                      value="Polygon"
                    />
                  </Box>
                )}
              </Box>
            )}

            {offrampStep === 'completed' && (
              <SuccessCard
                title={t('withdrawModal.offrampSuccessTitle', {
                  defaultValue: 'Offramp successful!',
                })}
                subtitle={t('withdrawModal.offrampSuccessDesc', {
                  defaultValue: 'Your fiat payout is being processed by Onramper.',
                })}
                txHash={offrampTxHash}
                onDone={hardClose}
              />
            )}
          </Box>
        </ScreenSwitcher>

        {/* ── Screen 3: wallet flow ───────────────────────────────────────── */}
        <ScreenSwitcher show={screen === 'wallet'}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Loading routes */}
            {!walletIsBusy && routesLoading && routes.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: '#10B981' }} />
              </Box>
            )}

            {/* Failed to load routes */}
            {!walletIsBusy && !routesLoading && routes.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', px: 3 }}>
                <Typography variant="body2" color="var(--error-main)" sx={{ mb: 1 }}>
                  {t('withdrawModal.couldNotLoadNetworks', {
                    defaultValue: 'Could not load supported networks',
                  })}
                </Typography>
                <Typography variant="caption" color="var(--text-muted)">
                  {t('withdrawModal.tryAgainLater', { defaultValue: 'Please try again later.' })}
                </Typography>
              </Box>
            )}

            {/* ── Form ── */}
            {!walletIsBusy && routes.length > 0 && walletStep === 'form' && (
              <>
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flex: 1,
                    overflowY: 'auto',
                  }}
                >
                  <FormField label={t('withdrawModal.recipientAddress')}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={recentAddrs}
                      inputValue={recipient}
                      onInputChange={(_, v) => {
                        setRecipient(v);
                        setQuote(null);
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-light)',
                        },
                      }}
                      slotProps={{ popper: { sx: { zIndex: 10002 } } }}
                      renderOption={(p, opt) => (
                        <li
                          {...p}
                          style={{
                            color: 'var(--text-primary)',
                            fontFamily: 'monospace',
                            fontSize: 12,
                          }}
                        >
                          {truncateAddr(opt)}
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          placeholder="0x..."
                          error={showAddrError}
                          helperText={
                            showAddrError
                              ? addrValidation.error
                              : recentAddrs.length > 0 && !recipient
                                ? t('withdrawModal.recentAddresses')
                                : ' '
                          }
                          sx={{
                            ...inputSx,
                            '& .MuiFormHelperText-root': {
                              color: showAddrError ? 'var(--error-main)' : 'var(--text-muted)',
                              ml: 0.5,
                              fontSize: '0.7rem',
                            },
                          }}
                        />
                      )}
                      sx={autocompleteSx}
                    />
                  </FormField>

                  <FormField label={t('withdrawModal.amount')}>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        placeholder="0.00"
                        type="number"
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value);
                          setQuote(null);
                        }}
                        size="small"
                        InputProps={{ inputProps: { min: 0, step: '0.01' } }}
                        sx={{ ...inputSx, '& input': { pr: 11 } }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="var(--text-muted)"
                          sx={{ fontWeight: 600 }}
                        >
                          pUSD
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setAmount(cashBalance.toFixed(6))}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            color: 'var(--text-primary)',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            bgcolor: 'var(--bg-secondary)',
                            '&:hover': { bgcolor: 'var(--border-light)' },
                          }}
                        >
                          Max
                        </Button>
                      </Box>
                    </Box>
                  </FormField>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1 }}>
                    <Typography variant="caption" color="var(--text-muted)">
                      {minCheckoutUsd > 0
                        ? t('withdrawModal.minAmount', { min: `$${minCheckoutUsd.toFixed(2)}` })
                        : ' '}
                    </Typography>
                    <Typography variant="caption" color="var(--text-muted)">
                      {t('withdrawModal.balance', { balance: `${cashBalance.toFixed(2)} pUSD` })}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {/* ── Token picker (first) ── */}
                    <FormField
                      label={t('withdrawModal.receiveToken')}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Autocomplete
                        size="small"
                        options={uniqueTokens}
                        getOptionLabel={(o) => o.symbol}
                        isOptionEqualToValue={(o, v) => o.symbol === v.symbol}
                        value={tokenSymbol ? { symbol: tokenSymbol } : null}
                        onChange={(_, v) => {
                          setTokenSymbol(v?.symbol ?? '');
                          setQuote(null);
                        }}
                        disabled={routesLoading}
                        disableClearable
                        ListboxProps={{
                          style: {
                            maxHeight: 240,
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-light)',
                          },
                        }}
                        slotProps={{ popper: { sx: { zIndex: 10002 } } }}
                        renderInput={(p) => (
                          <TextField
                            {...p}
                            placeholder={t('portfolio.searchPlaceholder', {
                              defaultValue: 'Search',
                            })}
                            sx={inputSx}
                          />
                        )}
                        renderOption={(props, opt) => (
                          <li
                            {...props}
                            key={opt.symbol}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              color: 'var(--text-primary)',
                              zIndex: 10001,
                            }}
                          >
                            <TokenBadge symbol={opt.symbol} />
                            {opt.symbol}
                          </li>
                        )}
                        sx={autocompleteSx}
                      />
                    </FormField>

                    {/* ── Chain picker (second) — greyed-out if token not supported ── */}
                    <FormField
                      label={t('withdrawModal.receiveChain')}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Autocomplete
                        size="small"
                        options={allChains}
                        getOptionLabel={(o) => o.chainName}
                        isOptionEqualToValue={(o, v) => o.chainId === v.chainId}
                        value={selectedChain}
                        onChange={(_, v) => {
                          if (!v || !chainsForToken.has(v.chainId)) return;
                          setChainId(v.chainId);
                          setQuote(null);
                        }}
                        getOptionDisabled={(o) => !chainsForToken.has(o.chainId)}
                        disabled={!tokenSymbol || routesLoading}
                        disableClearable
                        ListboxProps={{
                          style: {
                            maxHeight: 240,
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-light)',
                          },
                        }}
                        slotProps={{ popper: { sx: { zIndex: 10002 } } }}
                        renderInput={(p) => (
                          <TextField
                            {...p}
                            placeholder={t('portfolio.searchPlaceholder', {
                              defaultValue: 'Search',
                            })}
                            sx={inputSx}
                          />
                        )}
                        renderOption={(props, opt) => {
                          const supported = chainsForToken.has(opt.chainId);
                          const content = (
                            <Box
                              component="span"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                opacity: supported ? 1 : 0.38,
                                width: '100%',
                              }}
                            >
                              <ChainBadge chainName={opt.chainName} />
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  color: supported ? 'var(--text-primary)' : 'var(--text-muted)',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {opt.chainName}
                              </Typography>
                              {!supported && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}
                                >
                                  {t('withdrawModal.notSupported', {
                                    defaultValue: 'Not supported',
                                  })}
                                </Typography>
                              )}
                            </Box>
                          );
                          return (
                            <Tooltip
                              key={opt.chainId}
                              title={
                                supported
                                  ? ''
                                  : t('withdrawModal.notAvailableOn', {
                                      token: tokenSymbol,
                                      chain: opt.chainName,
                                      defaultValue: `${tokenSymbol} is not available on ${opt.chainName}`,
                                    })
                              }
                              placement="right"
                              arrow
                            >
                              <li
                                {...props}
                                style={{
                                  padding: '6px 12px',
                                  cursor: supported ? 'pointer' : 'not-allowed',
                                }}
                              >
                                {content}
                              </li>
                            </Tooltip>
                          );
                        }}
                        sx={autocompleteSx}
                      />
                    </FormField>
                  </Box>

                  {/* Quote section */}
                  <Box
                    sx={{
                      borderTop: '1px solid var(--border-light)',
                      pt: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" color="var(--text-muted)" fontWeight={600}>
                        {t('withdrawModal.youWillReceive')}
                      </Typography>
                      {quoteLoading ? (
                        <Skeleton
                          variant="text"
                          width={120}
                          sx={{ bgcolor: 'var(--bg-secondary)' }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="var(--text-primary)" fontWeight={700}>
                            {`${estReceiveAmount.toFixed(quote ? 6 : 2)} ${selectedToken?.symbol ?? ''}`}
                          </Typography>
                          {estOutputUsd !== null && (
                            <Typography variant="caption" color="var(--text-muted)">
                              ≈ ${estOutputUsd.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="var(--text-muted)">
                          {t('withdrawModal.transactionBreakdown')}
                        </Typography>
                        <Tooltip
                          title="Polymarket Bridge unwraps pUSD to USDC via Uniswap, then bridges to the destination chain. Settlement time and fees vary per chain."
                          placement="top"
                          arrow
                        >
                          <HelpOutlineIcon
                            sx={{ fontSize: 14, color: 'var(--text-muted)', cursor: 'help' }}
                          />
                        </Tooltip>
                      </Box>
                      {quoteLoading ? (
                        <Skeleton
                          variant="text"
                          width={80}
                          sx={{ bgcolor: 'var(--bg-secondary)' }}
                        />
                      ) : (
                        <Typography variant="caption" color="var(--text-muted)">
                          {feeUsd !== null && feeUsd > 0
                            ? t('withdrawModal.fee', {
                                fee: feeUsd.toFixed(2),
                                defaultValue: `Fee ~$${feeUsd.toFixed(2)}`,
                              })
                            : etaMin
                              ? `${t('withdrawModal.free')} · ~${etaMin} min`
                              : t('withdrawModal.free')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Sticky CTA */}
                <Box
                  sx={{
                    p: 3,
                    pt: 1,
                    borderTop: '1px solid var(--border-light)',
                    bgcolor: 'var(--bg-primary)',
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!ctaEnabled}
                    onClick={goToConfirm}
                    sx={{
                      py: 1.5,
                      bgcolor: ctaEnabled ? '#10B981' : 'var(--bg-secondary)',
                      color: ctaEnabled ? '#fff' : 'var(--text-muted)',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: ctaEnabled ? '#059669' : 'var(--border-light)' },
                      '&.Mui-disabled': {
                        color: 'var(--text-muted)',
                        bgcolor: 'var(--bg-secondary)',
                      },
                    }}
                  >
                    {ctaLabel}
                  </Button>
                </Box>
              </>
            )}

            {/* ── Confirm ── */}
            {!walletIsBusy && walletStep === 'confirm' && selectedToken && selectedChain && (
              <>
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flex: 1,
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="var(--text-muted)"
                    sx={{ textAlign: 'center', mb: 1 }}
                  >
                    {t('withdrawModal.reviewBeforeSigning', {
                      defaultValue: 'Please review before signing. This cannot be undone.',
                    })}
                  </Typography>

                  <ReviewRow
                    label={t('withdrawModal.amountLabel', { defaultValue: 'Amount' })}
                    value={`${amountNum.toFixed(2)} pUSD`}
                    subValue={`≈ $${amountNum.toFixed(2)}`}
                  />
                  <ReviewRow
                    label={t('withdrawModal.youWillReceive', { defaultValue: 'You will receive' })}
                    value={
                      quote
                        ? `${estReceiveAmount.toFixed(6)} ${selectedToken.symbol}`
                        : `${selectedToken.symbol}`
                    }
                    subValue={estOutputUsd !== null ? `≈ $${estOutputUsd.toFixed(2)}` : undefined}
                  />
                  <ReviewRow
                    label={t('withdrawModal.to', { defaultValue: 'To' })}
                    value={truncateAddr(recipient.trim())}
                    subValue={`${selectedChain.chainName} · ${selectedToken.symbol}`}
                    rightIcon={<ChainBadge chainName={selectedChain.chainName} />}
                  />
                  <ReviewRow
                    label={t('withdrawModal.estimatedTime', { defaultValue: 'Estimated time' })}
                    value={
                      etaMin
                        ? `~${etaMin} min`
                        : t('withdrawModal.defaultEstimatedTime', { defaultValue: '5–20 min' })
                    }
                  />
                  <ReviewRow
                    label={t('withdrawModal.feeLabel', { defaultValue: 'Fee' })}
                    value={
                      feeUsd !== null && feeUsd > 0
                        ? `~$${feeUsd.toFixed(2)}`
                        : t('withdrawModal.free', { defaultValue: 'Free' })
                    }
                  />
                </Box>

                <Box
                  sx={{
                    p: 3,
                    pt: 1,
                    borderTop: '1px solid var(--border-light)',
                    bgcolor: 'var(--bg-primary)',
                    flexShrink: 0,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setWalletStep('form')}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      textTransform: 'none',
                      borderColor: 'var(--border-light)',
                      color: 'var(--text-primary)',
                      '&:hover': { borderColor: 'var(--text-muted)' },
                    }}
                  >
                    {t('common.edit', { defaultValue: 'Edit' })}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={submit}
                    sx={{
                      flex: 2,
                      py: 1.5,
                      bgcolor: '#10B981',
                      '&:hover': { bgcolor: '#059669' },
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {t('withdrawModal.confirmAndSign', { defaultValue: 'Confirm & Sign' })}
                  </Button>
                </Box>
              </>
            )}

            {/* ── Success ── */}
            {walletIsBusy && bridgeStep === 'completed' && (
              <SuccessCard
                title={t('withdrawModal.withdrawalInitiated', {
                  defaultValue: 'Withdrawal initiated',
                })}
                subtitle={t('withdrawModal.withdrawalInitiatedDesc', {
                  defaultValue:
                    'pUSD sent to Polymarket Bridge. Funds will arrive at the destination in ~5–20 minutes.',
                })}
                txHash={bridgePolygonTxHash}
                onDone={hardClose}
              />
            )}

            {/* ── Failed ── */}
            {walletIsBusy && bridgeStep === 'failed' && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  py: 4,
                  px: 3,
                }}
              >
                <Typography variant="h6" color="var(--error-main)">
                  {t('withdrawModal.withdrawalFailed', { defaultValue: 'Withdrawal failed' })}
                </Typography>
                <Typography
                  variant="body2"
                  color="var(--text-muted)"
                  sx={{ textAlign: 'center', maxWidth: 320 }}
                >
                  {bridgeError ||
                    t('common.somethingWentWrong', { defaultValue: 'Something went wrong.' })}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    resetBridge();
                    setWalletStep('form');
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.retry', { defaultValue: 'Try again' })}
                </Button>
              </Box>
            )}

            {/* ── In flight ── */}
            {walletIsBusy && bridgeStep !== 'completed' && bridgeStep !== 'failed' && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  py: 4,
                }}
              >
                <CircularProgress size={48} sx={{ color: '#10B981' }} />
                <Typography variant="h6" fontWeight={700} color="var(--text-primary)">
                  {bridgeStep === 'preparing' &&
                    t('withdrawModal.preparingWithdrawal', {
                      defaultValue: 'Preparing withdrawal…',
                    })}
                  {bridgeStep === 'submitting' &&
                    t('withdrawModal.signInWallet', { defaultValue: 'Sign in your wallet' })}
                  {bridgeStep === 'polling' &&
                    t('withdrawModal.bridging', { defaultValue: 'Bridging to destination…' })}
                </Typography>
                <Typography
                  variant="body2"
                  color="var(--text-muted)"
                  sx={{ textAlign: 'center', maxWidth: 320 }}
                >
                  {bridgeStep === 'submitting'
                    ? t('withdrawModal.approveSafe', {
                        defaultValue: 'Approve the Safe transaction in the Privy prompt.',
                      })
                    : bridgeStep === 'polling'
                      ? t('withdrawModal.bridgingDesc', {
                          defaultValue:
                            'This can take a few minutes. You can close this dialog and check back later.',
                        })
                      : t('withdrawModal.settingUpWithdrawal', {
                          defaultValue: 'Setting up your withdrawal with Polymarket Bridge.',
                        })}
                </Typography>
              </Box>
            )}
          </Box>
        </ScreenSwitcher>

        {/* Close confirm overlay */}
        {closeConfirmOpen && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                maxWidth: 320,
                textAlign: 'center',
                bgcolor: 'var(--bg-primary)',
                p: 3,
                borderRadius: 2,
                border: '1px solid var(--border-light)',
              }}
            >
              <Typography
                variant="body1"
                color="var(--text-primary)"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                {t('withdrawModal.closeConfirmTitle')}
              </Typography>
              <Typography variant="body2" color="var(--text-muted)" sx={{ mb: 3 }}>
                {t('withdrawModal.closeConfirmDesc')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCloseConfirmOpen(false)}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    borderColor: 'var(--border-light)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('withdrawModal.keepOpen')}
                </Button>
                <Button
                  variant="contained"
                  onClick={hardClose}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    bgcolor: '#EF4444',
                    '&:hover': { bgcolor: '#DC2626' },
                  }}
                >
                  {t('withdrawModal.close')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────────

function ScreenSwitcher({ show, children }: { show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return (
    <Fade in timeout={180} appear>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>{children}</Box>
    </Fade>
  );
}

function MethodButton({
  icon,
  title,
  subtitle,
  onClick,
  disabled = false,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-[var(--border-color)] px-4 py-3.5 text-left transition-colors hover:bg-[var(--bg-secondary)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
    >
      <Box className="flex h-5 w-5 shrink-0 items-center justify-center text-[var(--text-primary)]">
        {icon}
      </Box>
      <Typography className="truncate text-sm font-semibold text-[var(--text-primary)]">
        {title}
        <Box component="span" className="mx-1 font-normal text-[var(--text-muted)] opacity-80">
          —
        </Box>
        <Box component="span" className="font-normal text-[var(--text-muted)] opacity-80">
          {subtitle}
        </Box>
      </Typography>
    </button>
  );
}

function BalancePill({ cashBalance }: { cashBalance: number }) {
  const { t } = useT();
  return (
    <Box className="mx-auto flex w-fit items-center gap-1.5 rounded-full border border-[var(--border-light)] bg-[var(--bg-secondary)] px-4 py-1.5 shadow-sm">
      <Typography className="text-xs font-medium text-[var(--text-muted)]">
        {t('withdrawModal.balanceLabel', { defaultValue: 'Balance ' })}
        <Box component="span" className="ml-1 font-bold text-[var(--text-primary)]">
          ${cashBalance.toFixed(2)}
        </Box>
      </Typography>
    </Box>
  );
}

function FormField({
  label,
  children,
  sx,
}: {
  label: string;
  children: React.ReactNode;
  sx?: object;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...sx }}>
      <Typography variant="caption" color="var(--text-primary)" fontWeight={600}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="caption" color="var(--text-muted)">
        {label}
      </Typography>
      <Typography variant="body2" color="var(--text-primary)">
        {value}
      </Typography>
    </Box>
  );
}

function ReviewRow({
  label,
  value,
  subValue,
  rightIcon,
}: {
  label: string;
  value: string;
  subValue?: string;
  rightIcon?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        borderBottom: '1px solid var(--border-light)',
      }}
    >
      <Typography variant="caption" color="var(--text-muted)">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textAlign: 'right' }}>
        {rightIcon}
        <Box>
          <Typography
            variant="body2"
            color="var(--text-primary)"
            fontWeight={600}
            sx={{
              fontFamily: value.startsWith('0x') || value.includes('…') ? 'monospace' : undefined,
              fontSize: value.startsWith('0x') || value.includes('…') ? '0.8rem' : undefined,
            }}
          >
            {value}
          </Typography>
          {subValue && (
            <Typography variant="caption" color="var(--text-muted)">
              {subValue}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function SuccessCard({
  title,
  subtitle,
  txHash,
  onDone,
}: {
  title: string;
  subtitle: string;
  txHash?: string | null;
  onDone: () => void;
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 4,
        px: 3,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <AnimatedCheck />
      </Box>
      <Typography variant="h6" fontWeight={700} color="var(--text-primary)" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="var(--text-muted)">
        {subtitle}
      </Typography>
      {txHash && (
        <Box sx={{ mt: 3, p: 1.5, bgcolor: 'rgba(16,185,129,0.05)', borderRadius: 1.5 }}>
          <Typography
            variant="caption"
            color="var(--text-muted)"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Polygon transaction
          </Typography>
          <Typography
            variant="body2"
            color="#10B981"
            sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {txHash}
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        fullWidth
        onClick={onDone}
        sx={{ mt: 4, bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none' }}
      >
        Done
      </Button>
    </Box>
  );
}

function AnimatedCheck() {
  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        '@keyframes drawCircle': {
          to: { strokeDashoffset: 0 },
        },
        '@keyframes drawTick': {
          to: { strokeDashoffset: 0 },
        },
        '@keyframes pop': {
          '0%': { transform: 'scale(0.9)' },
          '50%': { transform: 'scale(1.05)' },
          '100%': { transform: 'scale(1)' },
        },
        animation: 'pop 500ms ease-out',
      }}
    >
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle
          cx="32"
          cy="32"
          r="29"
          fill="rgba(16,185,129,0.1)"
          stroke="#10B981"
          strokeWidth="2"
          strokeDasharray="182"
          strokeDashoffset="182"
          style={{ animation: 'drawCircle 500ms ease-out forwards' }}
        />
        <path
          d="M18 33 L28 43 L46 22"
          fill="none"
          stroke="#10B981"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="48"
          strokeDashoffset="48"
          style={{ animation: 'drawTick 350ms 450ms ease-out forwards' }}
        />
      </svg>
    </Box>
  );
}

function ChainBadge({ chainName }: { chainName: string }) {
  const color = chainColor(chainName);
  const letter = chainName.charAt(0).toUpperCase();
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: color,
        color: '#fff',
        fontSize: '0.7rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {letter}
    </Box>
  );
}

function TokenBadge({ symbol }: { symbol: string }) {
  const color = tokenColor(symbol);
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: color,
        color: '#fff',
        fontSize: '0.65rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {symbol.charAt(0).toUpperCase()}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function detectAddressFormat(chainName: string): 'evm' | 'svm' | 'btc' | 'tron' | 'unknown' {
  const n = chainName?.toLowerCase();
  if (n.includes('solana')) return 'svm';
  if (n.includes('bitcoin') || n === 'btc') return 'btc';
  if (n.includes('tron') || n === 'trx') return 'tron';
  if (
    n.includes('ethereum') ||
    n.includes('polygon') ||
    n.includes('arbitrum') ||
    n.includes('optimism') ||
    n.includes('base') ||
    n.includes('avalanche') ||
    n.includes('bsc') ||
    n.includes('binance') ||
    n.includes('mainnet')
  ) {
    return 'evm';
  }
  return 'unknown';
}

function validateAddress(addr: string, chainName: string): { valid: boolean; error?: string } {
  const a = addr.trim();
  if (!a) return { valid: false };
  const fmt = detectAddressFormat(chainName);
  switch (fmt) {
    case 'evm':
      if (!/^0x[a-fA-F0-9]{40}$/.test(a)) {
        return { valid: false, error: 'Must be a 0x address (42 characters)' };
      }
      return { valid: true };
    case 'svm':
      if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a)) {
        return { valid: false, error: 'Invalid Solana address' };
      }
      return { valid: true };
    case 'btc':
      if (!/^(bc1[a-zA-HJ-NP-Z0-9]{6,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(a)) {
        return { valid: false, error: 'Invalid Bitcoin address' };
      }
      return { valid: true };
    case 'tron':
      if (!/^T[A-Za-z1-9]{33}$/.test(a)) {
        return { valid: false, error: 'Invalid Tron address (must start with T)' };
      }
      return { valid: true };
    default:
      return { valid: true };
  }
}

function truncateAddr(a: string): string {
  if (a.length <= 16) return a;
  return `${a.slice(0, 8)}…${a.slice(-6)}`;
}

function getRecentAddresses(chainId: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(`withdraw:recent:${chainId}`);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveRecentAddress(chainId: string, addr: string) {
  if (typeof window === 'undefined') return;
  try {
    const existing = getRecentAddresses(chainId);
    const filtered = existing.filter((a) => a?.toLowerCase() !== addr?.toLowerCase());
    const updated = [addr, ...filtered].slice(0, RECENT_ADDR_LIMIT);
    window.localStorage.setItem(`withdraw:recent:${chainId}`, JSON.stringify(updated));
  } catch {
    /* ignore quota errors */
  }
}

const CHAIN_COLORS: Record<string, string> = {
  ethereum: '#627EEA',
  polygon: '#8247E5',
  arbitrum: '#28A0F0',
  base: '#0052FF',
  optimism: '#FF0420',
  solana: '#9945FF',
  bitcoin: '#F7931A',
  tron: '#FF060A',
  bsc: '#F0B90B',
  binance: '#F0B90B',
  avalanche: '#E84142',
};

function chainColor(chainName: string): string {
  const key = chainName?.toLowerCase();
  for (const k of Object.keys(CHAIN_COLORS)) {
    if (key.includes(k)) return CHAIN_COLORS[k];
  }
  let h = 0;
  for (let i = 0; i < chainName.length; i++) {
    h = (h << 5) - h + chainName.charCodeAt(i);
    h |= 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 60%, 45%)`;
}

const TOKEN_COLORS: Record<string, string> = {
  USDC: '#2775CA',
  USDT: '#26A17B',
  PUSD: '#10B981',
  PYUSD: '#0070BA',
  DAI: '#F5AC37',
  ETH: '#627EEA',
  BTC: '#F7931A',
  SOL: '#9945FF',
  TRX: '#FF060A',
  WBTC: '#F7931A',
};

function tokenColor(symbol: string): string {
  const upper = symbol.toUpperCase();
  if (TOKEN_COLORS[upper]) return TOKEN_COLORS[upper];
  let h = 0;
  for (let i = 0; i < upper.length; i++) {
    h = (h << 5) - h + upper.charCodeAt(i);
    h |= 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 50%, 45%)`;
}

/**
 * Token decimals lookup for converting bridge `estToTokenBaseUnit` to human amount.
 * If the backend ever sends `decimals` per route, prefer that and fall back to this map.
 */
function getTokenDecimals(symbol: string): number {
  const s = symbol.toUpperCase();
  if (!s) return 6;
  if (s === 'BTC' || s === 'WBTC') return 8;
  if (s === 'SOL') return 9;
  if (
    s === 'ETH' ||
    s === 'WETH' ||
    s === 'MATIC' ||
    s === 'POL' ||
    s === 'DAI' ||
    s === 'AVAX' ||
    s === 'BNB'
  )
    return 18;
  // Stablecoins (USDC, USDT, pUSD, PYUSD, USDC.e, etc.) default to 6
  return 6;
}

// ────────────────────────────────────────────────────────────────────────────
// Shared styles
// ────────────────────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiInputBase-root': {
    color: 'var(--text-primary)',
    bgcolor: 'var(--bg-secondary)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--border-light)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--text-muted)',
  },
  '& input::placeholder': { color: 'var(--text-muted)', opacity: 0.7 },
};

const autocompleteSx = {
  '& .MuiInputBase-root': {
    color: 'var(--text-primary)',
    bgcolor: 'var(--bg-secondary)',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-light)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-muted)' },
  '& .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator': { color: 'var(--text-muted)' },
  '& input::placeholder': { color: 'var(--text-muted)', opacity: 0.7 },
};
